import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Settings, Send, RefreshCcw } from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";
import AccountUpsell from "@/components/AccountUpsell";
import EmailFrame from "@/components/EmailFrame";
import ForecastLoader from "@/components/ForecastLoader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REMEMBER_KEY = "weathervane:last-token";

/**
 * Permanent bookmarkable forecast view. Shows the same HTML the user gets
 * by email, but rendered live for ALL of their active locations at once.
 *
 * On first successful load we stash the manage_token in localStorage so the
 * landing page can offer a quick-return banner on future visits.
 */
export default function TodayPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const firedRef = useRef(false);

  // Cancel the email template's dark-mode logo invert in this in-browser
  // context so the logo stays black on the white email background — email
  // clients still get the invert because they don't load this page.
  //
  // /today/ is the "view this email in your browser" experience, so we
  // KEEP the email's hero/greeting/footer chrome here (unlike /app where
  // those are hidden because the React dashboard already shows them).
  // Font sizing is handled dynamically inside EmailFrame.js via
  // scale-aware inverse-scaling injection.
  const _prepareDoc = (h) => {
    if (!h) return "";
    let out = h;
    out = out.replace(/<head([^>]*)>/i, '<head$1><base target="_top">');
    const tailStyle =
      '<style>' +
      '@media (prefers-color-scheme: dark) {' +
      '  .wv-logo-img { filter: none !important; }' +
      '  .wv-logo-dark { display:none !important; }' +
      '  .wv-logo-light { display:block !important; }' +
      '  body { background:#F9F9F9 !important; }' +
      '}' +
      // Banter is the editorial one-liner — per product decision, it
      // belongs only in the inbox email. Hide it in this "view in
      // browser" context so /today reads as the user's own workspace.
      '.wv-email-banter{display:none !important;}' +
      '</style>';
    out = out.replace(/<\/head>/i, `${tailStyle}</head>`);
    return out;
  };

  const load = async (silent = false, forceRefresh = false) => {
    // ALWAYS show the full ForecastLoader animation on any reload event —
    // the spinning Refresh icon alone made the button look broken during
    // the 10-15s wait. Clearing `html` ensures the loader is what's on
    // screen until fresh content arrives.
    if (forceRefresh) {
      setHtml("");
    }
    setLoading(true);
    setRefreshing(forceRefresh || silent);
    setError("");
    try {
      // Combined preview: ONE email covering all active locations. Mirrors
      // the scheduler's daily email shape (single header, single footer,
      // location banners inline) — and avoids the old per-sub stacking
      // which produced the trailing whitespace on mobile.
      if (forceRefresh) {
        await axios.post(
          `${API}/manage/${token}/subscriptions/preview/refresh`,
        ).catch(() => {});
      }
      const r = await axios.post(`${API}/manage/${token}/subscriptions/preview`);
      setHtml(_prepareDoc(r.data?.html || ""));
      try { localStorage.setItem(REMEMBER_KEY, token); } catch {}
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 404 && detail === "No active locations") {
        setError("You don't have any active weather subscriptions yet.");
      } else {
        setError(detail || "Couldn't load your forecast right now.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const sendNow = async () => {
    try {
      await axios.post(`${API}/manage/${token}/send-now`);
      navigate(`/send-now/${token}`);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10 sticky top-0 bg-[#F9F9F9]/95 backdrop-blur-sm z-30">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <RoosterLogo size={41} />
            <div className="min-w-0">
              <div className="font-display text-xl font-black tracking-tighter truncate leading-none">Weathervane</div>
              <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-neutral-500 font-bold mt-1 truncate">Weather &amp; News <span className="mx-1">/</span> <span className="italic">BYO Coffee</span></div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(true, true)}
              disabled={loading || refreshing}
              data-testid="today-refresh"
              title="Refresh forecast"
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.14em] uppercase font-semibold border border-black bg-white hover:bg-[#FFD600] transition-colors"
              style={{ borderRadius: 6 }}
            >
              <RefreshCcw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={sendNow}
              data-testid="today-send-now"
              title="Email this to me right now"
              className="btn-brutal inline-flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.14em] uppercase font-semibold"
            >
              <Send className="w-3 h-3" /> <span className="hidden sm:inline">Email me</span>
            </button>
            <Link
              to={`/manage/${token}`}
              data-testid="today-manage"
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.14em] uppercase font-semibold border border-black bg-white hover:bg-[#FFD600] transition-colors"
              style={{ borderRadius: 6 }}
            >
              <Settings className="w-3 h-3" /> <span className="hidden sm:inline">Manage</span>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-2 sm:px-6 lg:px-12 py-6" data-testid="today-page">
        <AccountUpsell next={`/today/${token}`} className="mb-5" />
        {loading && (
          <div data-testid="today-loading">
            <ForecastLoader label="Building your forecast" />
          </div>
        )}

        {!loading && error && (
          <div className="border border-black bg-white p-10 max-w-xl mx-auto" data-testid="today-error">
            <div className="ticker-label mb-2">Hmm</div>
            <h1 className="font-display text-3xl font-black tracking-tighter">{error}</h1>
            <Link to={`/manage/${token}`} className="btn-brutal inline-flex items-center gap-2 px-5 py-3 mt-6 text-xs tracking-[0.14em] uppercase font-semibold">
              <Settings className="w-3 h-3" /> Manage subscriptions
            </Link>
          </div>
        )}

        {!loading && !error && html && (
          <div className="bg-white border border-black/10" style={{ borderRadius: 6 }} data-testid="today-iframe">
            <EmailFrame html={html} wrapperId="today-iframe-wrapper" />
          </div>
        )}
      </section>
    </div>
  );
}
